import React, { useState, useEffect } from 'react';
import voca from 'voca';
import CustomLoader from '../common/CustomLoader';
import Swal from 'sweetalert2';

const PreviewProjectModal = ({
  project,
  previewModalOpen,
  togglePreview,
  generateProjectURL,
  selVersion,
  handleVersionChange,
  versions,
  publishApp,
  isPublishing,
  slug,
  version,
  actions,
  projectId,
  isReadOnly,
}) => {
  const [debugModeState, setDebugModeState] = useState({});
  const [cleanPublishState, setCleanPublishState] = useState({});

  let { domainName, seoName, debugMode } = project;
  const url = generateProjectURL('PREVIEW', domainName, seoName, slug, true);

  useEffect(() => {
    const initialDebugState = {};
    const initialCleanState = {};

    if (debugMode) {
      initialDebugState['PREVIEW'] = {
        isEnable: debugMode.includes('PREVIEW'),
        environment: 'PREVIEW',
      };
    }

    // Same default behavior as Publish modal
    initialCleanState['PREVIEW'] = true;

    setDebugModeState(initialDebugState);
    setCleanPublishState(initialCleanState);
  }, [debugMode]);

  const handlePublishClick = async () =>
    await publishApp('PREVIEW', 'PREVIEW', cleanPublishState['PREVIEW']);

  const handleDebugModeToggle = async (envType) => {
    const currentState = debugModeState[envType] ? debugModeState[envType].isEnable : false;

    const newState = !currentState;

    const heading = `Are you sure to ${
      newState ? 'enable' : 'disable'
    } debug mode for ${voca.titleCase(envType)}?`;

    const description = newState
      ? 'It will start displaying logs in the browser console window.'
      : 'It will stop displaying logs in the browser console window.';

    const result = await Swal.fire({
      title: heading,
      text: description,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: newState ? '#3085d6' : '#d33',
      cancelButtonColor: '#757f91',
      confirmButtonText: `Yes, ${newState ? 'enable' : 'disable'} it!`,
    });

    if (result.isConfirmed) {
      const newStateObject = {
        isEnable: newState,
        environment: envType,
      };

      setDebugModeState({
        ...debugModeState,
        [envType]: newStateObject,
      });

      await actions.toggleDebugMode({
        projectId,
        version,
        data: newStateObject,
      });
    }
  };

  const handleCleanToggle = (envType) => {
    setCleanPublishState({
      ...cleanPublishState,
      [envType]: !cleanPublishState[envType],
    });
  };

  const appEnv = process.env.REACT_APP_ENVIRONMENT;

  return (
    <div
      className="gjs-mdl-container publish"
      style={{
        display: !previewModalOpen ? 'none' : '',
      }}
    >
      <div className="gjs-mdl-dialog gjs-one-bg gjs-two-color">
        <div className="gjs-mdl-header">
          <div className="d-flex justify-content-between" style={{ width: '96%' }}>
            <div className="gjs-mdl-title">Publish your app for the preview.</div>
          </div>
          <div className="gjs-mdl-btn-close" onClick={togglePreview} data-close-modal="">
            ⨯
          </div>
        </div>

        <div className="gjs-mdl-content">
          <div id="gjs-mdl-c">
            <div className="row pt-2 pb-2">
              <div className="col-md-12 col-sm-12">
                {appEnv !== 'PRODUCTION' ? (
                  'Publish in preview on staging is disabled. Please publish on Production'
                ) : (
                  <table className="table table-stripped table-bordered table-hover">
                    <thead>
                      <tr>
                        <th>Server</th>
                        <th>URL</th>
                        <th>Versions</th>
                        <th>Debug</th>
                        <th>Clean Publish</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{voca.titleCase('PREVIEW')}</td>

                        <td style={{ wordBreak: 'break-all' }}>
                          <p>{generateProjectURL('PREVIEW', domainName, seoName, slug)}</p>
                        </td>

                        <td>
                          <select
                            name="selVersion"
                            className="form-control"
                            value={selVersion['PREVIEW']}
                            onChange={(event) => handleVersionChange(event, 'PREVIEW')}
                          >
                            <option value="">Select Version</option>
                            {versions.map((version) => (
                              <option value={version.currentVersion} key={version.currentVersion}>
                                v{version.currentVersion}
                              </option>
                            ))}
                          </select>
                        </td>

                        <td>
                          <div className="custom-control custom-switch ml-3">
                            <input
                              type="checkbox"
                              className="custom-control-input"
                              id="debugModeToggle-PREVIEW"
                              checked={
                                debugModeState['PREVIEW']
                                  ? debugModeState['PREVIEW'].isEnable
                                  : false
                              }
                              onChange={() =>
                                isReadOnly ? null : handleDebugModeToggle('PREVIEW')
                              }
                              disabled={isReadOnly}
                              title={isReadOnly && 'Read-Only Mode'}
                            />
                            <label
                              className="custom-control-label"
                              htmlFor="debugModeToggle-PREVIEW"
                            ></label>
                          </div>
                        </td>

                        <td>
                          <div className="custom-control custom-switch ml-3">
                            <input
                              type="checkbox"
                              className="custom-control-input"
                              id="cleanPublishToggle-PREVIEW"
                              checked={cleanPublishState['PREVIEW']}
                              onChange={() => (isReadOnly ? null : handleCleanToggle('PREVIEW'))}
                              disabled={isReadOnly}
                              title={isReadOnly && 'Read-Only Mode'}
                            />
                            <label
                              className="custom-control-label"
                              htmlFor="cleanPublishToggle-PREVIEW"
                            ></label>
                          </div>
                        </td>

                        <td>
                          <div className="d-flex justify-content-center my-3">
                            <button
                              type="button"
                              className="btn btn-theme-submit btn-sm"
                              onClick={() => window.open(url)}
                            >
                              <i className="wb webconnect-eye-fill"></i>&nbsp;View
                            </button>
                            &nbsp;
                            <button
                              type="button"
                              className="btn btn-theme-submit btn-sm"
                              onClick={isPublishing ? null : handlePublishClick}
                              disabled={isPublishing || isReadOnly}
                              title={isReadOnly && 'Read-Only Mode'}
                            >
                              <i className="wb webconnect-paper-plane-fill"></i>
                              &nbsp;Publish
                              <CustomLoader loading={isPublishing} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="gjs-mdl-collector"></div>
      </div>
    </div>
  );
};

export default PreviewProjectModal;
